import { FeedbackContent } from '@shared/components/feedback-content/FeedbackContent';
import { InternalServerErrorIllustration } from '@shared/components/feedback-content/illustrations/InternalServerErrorIllustration';
import { navigateToHelp } from '@shared/helpers/navigate-to-help/navigateToHelp';
import { Component, type ReactNode } from 'react';

import type {
  ErrorBoundaryProps,
  ErrorBoundaryState,
} from './ErrorBoundary.types';

/**
 * Top-level ErrorBoundary for catching unhandled errors in the React tree.
 *
 * Placement: Wraps AuthProvider in App.tsx — catches crashes from
 * AuthProvider, UserProvider, PermissionsProvider, and all route components.
 *
 * FeedbackContent is purely presentational (no router dependency),
 * so no MemoryRouter wrapper is needed. Navigation uses window.location.href.
 *
 * Shows "Get help" (opens support) and "Log out" (clears session, redirects).
 * Cannot use useLogout hook (class component), so logout is manual.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Unhandled error:', error, errorInfo);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      const authType = globalThis.sessionStorage?.getItem('auth-type');
      const showActions = authType !== 'b2e';

      const handleGetHelp = (): void => {
        navigateToHelp();
      };

      const handleLogout = (): void => {
        sessionStorage.clear();
        globalThis.window.location.href = '/';
      };

      return (
        <FeedbackContent
          title="Our bad!"
          message="Something went wrong on our servers. Try to refresh the page or contact us if the problem persists."
          illustration={<InternalServerErrorIllustration />}
          onPrimaryAction={handleGetHelp}
          primaryActionLabel="Get help"
          showPrimaryAction={showActions}
          secondaryActionLabel="Log out"
          onSecondaryAction={handleLogout}
          showSecondaryAction={showActions}
        />
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
